import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { IndexTableHead } from './IndexTableHead';
import { IndexHoverableRow } from './rows/hoverable-row/IndexHoverableRow';
import { useIndexTableContext } from './index_table.context';
import { getComparator } from '../../utils/table.util';
import { getIn } from 'formik';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.12)',
  },
}));

export const IndexTableContainer = () => {
  const {
    headCells,
    filteredData,
    checkboxes = false,
    expandOnCheckbox = false,
    icons,
    iconHead,
    emptyRows = 0,
    selected = [],
    setSelected = () => {},
    selectMultiple,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    onRowClick,
    hoverCell,
    defaultCheckboxFilters,
    checkboxFilters,
    textFilters,
    hideclearFilters,
    clearFilters,
    exportFormat,
    title,
    itemBorder,
    page,
    rowsPerPage,
    tableProps,
  } = useIndexTableContext();

  const rows = useMemo(() => {
    const emptyCells = filteredData?.filter(x => !getIn(x, orderBy));
    const populatedCells = filteredData?.filter(x => !!getIn(x, orderBy));
    return [
      ...populatedCells?.slice()?.sort(getComparator(order, orderBy)),
      ...emptyCells,
    ]?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [order, orderBy, page, rowsPerPage, filteredData]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'asc' : 'desc');
    setOrderBy(property);
  };
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows?.map(n => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleCheckboxClick = (id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  return (
    <TableContainer
      sx={{
        width: '100%',
        mx: 'auto',
        boxSizing: 'border-box',
        maxHeight: tableProps?.stickyHeader ? '100%' : undefined,
      }}
    >
      <Table
        sx={
          itemBorder
            ? {
                minWidth: 750,
                // padding: '10px',
                borderCollapse: 'separate',
                borderSpacing: '0 5px',
              }
            : {
                minWidth: 750,
                // padding: '10px',
              }
        }
        size={'small'}
        {...tableProps}
      >
        <IndexTableHead numSelected={selected.length} rowCount={rows?.length} />
        <TableBody>
          {rows.map((row, index) =>
            row.rowRender ? (
              row.rowRender(row, index)
            ) : (
              <IndexHoverableRow
                key={`hoverable-table-row-${index}`}
                row={row}
              />
            )
          )}
          {!itemBorder && emptyRows > 0 && (
            <StyledTableRow
              style={{
                height: 53 * emptyRows,
              }}
            >
              <TableCell colSpan={12} />
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
